/**
 * `SourceDocumentsTab` — list the source documents for a node. Each row
 * opens the universal `DocumentDetailOverlay` above the current workspace
 * (so the analyst stays in Graph / Map / Insights). Falls back to a new-tab
 * open when only a file path is known.
 */

import { ExternalLink, FileText } from 'lucide-react'
import { useDocumentOverlayOptional } from '@/components/document-overlay/DocumentDetailOverlayContext'
import { Button } from '@/components/ui/button'

export interface SourceDocumentEntry {
  /** Neo4j `elementId` of the document node — useful as a stable key, but
   * NOT a valid `document_id` for case document endpoints. Use `document_id`
   * when navigating to the document viewer. */
  id?: string
  /** UUID of the case document (`CaseDocument.id`). What the documents
   * route expects as `?doc=…`. */
  document_id?: string
  name?: string
  path?: string
}

export interface SourceDocumentsTabProps {
  sourceDocuments?: SourceDocumentEntry[] | null
}

// Only absolute API URLs (/api/v1/files/<s3_key>) are servable; pre-v2
// filesystem paths have no backing mount anymore.
function buildViewUrl(path: string | undefined): string | null {
  if (!path) return null
  return path.startsWith('/api/') ? path : null
}

export function SourceDocumentsTab({ sourceDocuments }: SourceDocumentsTabProps) {
  const docs = sourceDocuments ?? []
  const overlay = useDocumentOverlayOptional()

  if (docs.length === 0) {
    return (
      <p className="rounded-lg border border-border px-3 py-6 text-center text-xs text-muted-foreground">
        No source documents.
      </p>
    )
  }

  return (
    <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border">
      {docs.map((doc, idx) => {
        const fileName = doc.name || doc.path?.split('/').pop() || 'Unknown document'
        const fallbackUrl = buildViewUrl(doc.path)
        // The overlay expects the case document UUID, not the Neo4j elementId.
        const navId = doc.document_id
        // Open the overlay only when we have both a document_id and the
        // overlay provider is mounted (we are inside a case shell). Otherwise
        // fall back to the file path or the disabled state.
        const canOpenOverlay = Boolean(navId && overlay)

        const handleClick = () => {
          if (canOpenOverlay && navId && overlay) {
            overlay.open(navId)
          } else if (fallbackUrl) {
            window.open(fallbackUrl, '_blank', 'noopener,noreferrer')
          }
        }

        const interactive = canOpenOverlay || Boolean(fallbackUrl)

        return (
          <li key={doc.id ?? `${fileName}-${idx}`}>
            {interactive ? (
              <Button
                variant="ghost"
                onClick={handleClick}
                className="h-auto w-full justify-start gap-2 rounded-none px-3 py-2.5 text-left text-xs font-normal text-foreground hover:bg-muted/60"
                title={canOpenOverlay ? 'Open document' : 'Open document in new tab'}
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate">{fileName}</span>
                {canOpenOverlay ? null : (
                  <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                )}
              </Button>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2.5 text-xs text-muted-foreground">
                <FileText className="h-4 w-4 shrink-0" />
                <span className="flex-1 truncate">{fileName}</span>
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}
